import HighlightedText from 'Components/Atoms/HighlightedText/HighlightedText';
import Loader, { LOADER_SIZE } from 'Components/Atoms/Loader/Loader';
import NoValue from 'Components/Atoms/NoValue/NoValue';
import React, { useEffect, useRef, useState } from 'react';
import { getAccessTokenFromLocal, useOuterClick } from 'Services/helpers';
import { API_URL } from 'Store/Shared/Middleware/API/Constants';
import styles from './AddressField.module.sass';

export const formatAddress = (address: any) => {
    if (!address) return '';
    
    let street = '';
    if (address?.street_number) {
        street += address.street_number;
    }
    if (address?.route) {
        if (street.length > 0) {
            street = address.route + ' ' + street + ', ';
        } else {
            street = address.route + ', ';
        }
    }
    let locality = '';
    if (address?.postal_code) {
        locality += address.postal_code;
    }
    if (address?.locality) {
        locality += ' ' + address.locality + ', ';
    }

    return street + ' ' + locality + ' ' + address?.country;
}

export const formatStreet = (address: any) => {
    if (!address) return '';
    
    let street = '';
    if (address?.street_number) {
        street += address.street_number;
    }
    if (address?.route) {
        if (street.length > 0) {
            street = address.route + ' ' + street;
        } else {
            street = address.route;
        }
    }

    return street;
}

export const formatLocality = (address: any) => {
    if (!address) return '';
    
    let locality = '';
    if (address?.postal_code) {
        locality += address.postal_code;
    }
    if (address?.locality) {
        locality += ' ' + address.locality;
    }

    return locality;
}

const AddressField = ({isEdit, onChange, value: rawValue, fieldProperties, ...props}: any) => {
    const isEmpty = !rawValue;
    const debounceDelay = 200;
    const [state, setState] = useState({
        isFocused: false,
        isLoading: false
    });
    const [searchQuery, setSearchQuery] = useState(`${formatStreet(rawValue?.address ?? rawValue) && `${formatStreet(rawValue?.address ?? rawValue)}, `}${formatLocality(rawValue?.address ?? rawValue) && `${formatLocality(rawValue?.address ?? rawValue)}, `}${rawValue?.address?.country ?? rawValue?.country ?? ''}` ?? '');
    const [data, setData] = useState({
        value: rawValue?.value,
        entities: []
    })
    
    const inputRef: any = useRef();
    const innerRef = useOuterClick(() => setState({...state, isFocused: false}));
    const handleFocusClick = () => {
        setState({...state, isFocused: true});
        setSearchQuery('');
        setTimeout(() => inputRef?.current?.focus(), 100);
    }

    const [timer, setTimer] = useState<any>(null);
    const handleValueChange = (e) => setSearchQuery(e.target.value);

    const handleSelect = (item) => {
        setData({...data, value: item});
        onChange(item);
        setState({...state, isFocused: false});
        setSearchQuery('');
    }

    useEffect(() => {
        if (!isEdit) return;

        clearTimeout(timer);
        setTimer(null);

        if (!state.isLoading) {
            setState({...state, isLoading: true});
        }

        if (searchQuery.length <= 0) {
            setState({...state, isLoading: false});
            setData({...data, entities: []});
            return;
        }

        setTimer(
            setTimeout(() => {
                const accessToken = getAccessTokenFromLocal();

                fetch(`${API_URL}/api/search?fields=maps&input=${searchQuery}&limit=10`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                }).then(async (item: any) => {
                    const response = await item.json();
                    setState({...state, isLoading: false});

                    const results = response?.data?.maps || [];
                    setData({...data, entities: results});
                });
            }, debounceDelay)
        );
    }, [searchQuery]);

    if (!isEdit) {
        if (isEmpty) {
            return <NoValue />;
        }

        if (fieldProperties?.isInline) {

            return <>{formatStreet(rawValue?.address ?? rawValue)}, {formatLocality(rawValue?.address ?? rawValue)}, {rawValue?.address?.country ?? rawValue?.country}</>;
        }

        return <>
            { formatStreet(rawValue?.address ?? rawValue) && <>{ formatStreet(rawValue?.address ?? rawValue) }<br /></> }
            { formatLocality(rawValue?.address ?? rawValue) && <>{ formatLocality(rawValue?.address ?? rawValue) }<br /></> }
            { rawValue?.address?.country ?? rawValue?.country }
        </>;
    }

    return <div className={`${styles['addressfield']} ${state.isFocused ? styles['addressfield--active'] : ''}`} ref={innerRef}>
        <div className={styles['addressfield__content']} onClick={handleFocusClick}>
            { !isEmpty && formatAddress(data?.value?.address) }
            <input
                placeholder={'Typ hier een adres in...'}
                value={searchQuery}
                onChange={handleValueChange}
                ref={inputRef}
            />
        </div>
        { state.isFocused && searchQuery.length > 0 && <div className={styles['addressfield__dropdown']}>
            { state.isLoading && <Loader size={LOADER_SIZE.MEDIUM} /> }
            { !state.isLoading &&
                data.entities.length == 0 &&
                <div className={styles['dropdown__item']} style={{pointerEvents: 'none'}}>
                    Geen resultaten gevonden...
                </div>
            }
            { !state.isLoading && data.entities.map((item: any) =>
                <div className={styles['dropdown__item']} onClick={() => handleSelect(item)}>
                    <HighlightedText highlight={searchQuery}>{formatAddress(item?.address)}</HighlightedText>
                </div>
            )}
        </div> }
    </div>
};

export default AddressField;